/**
 * MINDSHIFT MASTERZ - INTERACTIVE ELEMENTS
 * Modern UX interactions and animations
 * Version: 2.0
 * Date: 2025-12-02
 */

(function() {
  'use strict';

  // ============================================
  // 1. SCROLL PROGRESS BAR
  // ============================================
  function initScrollProgress() {
    const progressBar = document.createElement('div');
    progressBar.className = 'scroll-progress';
    document.body.prepend(progressBar);

    window.addEventListener('scroll', () => {
      const windowHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrolled = (window.scrollY / windowHeight) * 100;
      progressBar.style.width = scrolled + '%';
    }, { passive: true });
  }

  // ============================================
  // 2. SCROLL ANIMATIONS (Intersection Observer)
  // ============================================
  function initScrollAnimations() {
    const observerOptions = {
      threshold: 0.15,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Observe all elements with scroll-animate class
    document.querySelectorAll('.scroll-animate').forEach(el => {
      observer.observe(el);
    });

    // Stagger animation for product cards
    const productCards = document.querySelectorAll('.product-card');
    productCards.forEach((card, index) => {
      card.style.animationDelay = `${index * 0.1}s`;
      observer.observe(card);
    });
  }

  // ============================================
  // 3. COUNTDOWN TIMER
  // ============================================
  function initCountdownTimer() {
    const countdownElement = document.querySelector('.countdown-numbers');
    if (!countdownElement) return;

    // Set end time (24 hours from now)
    const endTime = new Date().getTime() + (24 * 60 * 60 * 1000);

    function updateCountdown() {
      const now = new Date().getTime();
      const distance = endTime - now;

      if (distance < 0) {
        countdownElement.textContent = '00:00:00';
        return;
      }

      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      countdownElement.textContent =
        `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

      // Add urgency glow when less than 1 hour
      if (hours === 0) {
        countdownElement.style.color = '#ff4444';
        countdownElement.style.textShadow = '0 0 20px rgba(255, 68, 68, 0.6)';
      }
    }

    updateCountdown();
    setInterval(updateCountdown, 1000);
  }

  // ============================================
  // 4. FAQ ACCORDION
  // ============================================
  function initFAQAccordion() {
    const faqItems = document.querySelectorAll('.faq-question');

    faqItems.forEach(question => {
      question.addEventListener('click', () => {
        const faqItem = question.parentElement;
        const isActive = faqItem.classList.contains('active');

        // Close all other items
        document.querySelectorAll('.faq-item').forEach(item => {
          item.classList.remove('active');
        });

        // Toggle current item
        if (!isActive) {
          faqItem.classList.add('active');
        }
      });
    });
  }

  // ============================================
  // 5. BACK TO TOP BUTTON
  // ============================================
  function initBackToTop() {
    const backToTopBtn = document.createElement('button');
    backToTopBtn.className = 'back-to-top';
    backToTopBtn.innerHTML = '↑';
    backToTopBtn.setAttribute('aria-label', 'Back to top');
    document.body.appendChild(backToTopBtn);

    window.addEventListener('scroll', () => {
      if (window.scrollY > 500) {
        backToTopBtn.classList.add('visible');
      } else {
        backToTopBtn.classList.remove('visible');
      }
    }, { passive: true });

    backToTopBtn.addEventListener('click', () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    });
  }

  // ============================================
  // 6. LAZY LOADING IMAGES (Blur-up effect)
  // ============================================
  function initLazyLoading() {
    const lazyImages = document.querySelectorAll('img[loading="lazy"]');

    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.classList.add('loaded');
            imageObserver.unobserve(img);
          }
        });
      });

      lazyImages.forEach(img => {
        img.classList.add('lazy-image');
        imageObserver.observe(img);
      });
    } else {
      // Fallback for browsers without IntersectionObserver
      lazyImages.forEach(img => {
        img.classList.add('loaded');
      });
    }
  }

  // ============================================
  // 7. RIPPLE EFFECT ON BUTTONS
  // ============================================
  function initRippleEffect() {
    const buttons = document.querySelectorAll('.cta-primary, .product-card-cta');

    buttons.forEach(button => {
      button.addEventListener('click', function(e) {
        const ripple = document.createElement('span');
        const rect = this.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height);
        const x = e.clientX - rect.left - size / 2;
        const y = e.clientY - rect.top - size / 2;

        ripple.style.width = ripple.style.height = size + 'px';
        ripple.style.left = x + 'px';
        ripple.style.top = y + 'px';
        ripple.classList.add('ripple-effect');

        this.appendChild(ripple);

        setTimeout(() => ripple.remove(), 600);
      });
    });
  }

  // ============================================
  // 8. PRODUCT CARD HOVER 3D TILT
  // ============================================
  function initCardTilt() {
    const cards = document.querySelectorAll('.product-card');

    cards.forEach(card => {
      card.addEventListener('mousemove', handleTilt);
      card.addEventListener('mouseleave', resetTilt);
    });

    function handleTilt(e) {
      const card = e.currentTarget;
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const centerX = rect.width / 2;
      const centerY = rect.height / 2;

      const rotateX = (y - centerY) / 10;
      const rotateY = (centerX - x) / 10;

      card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-10px)`;
    }

    function resetTilt(e) {
      const card = e.currentTarget;
      card.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) translateY(0)';
    }
  }

  // ============================================
  // 9. SMOOTH ANCHOR SCROLLING
  // ============================================
  function initSmoothAnchors() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const href = this.getAttribute('href');
        if (href === '#') return;

        e.preventDefault();
        const target = document.querySelector(href);

        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  }

  // ============================================
  // 10. ADD TO CART NOTIFICATION
  // ============================================
  function initAddToCartNotification() {
    const addToCartButtons = document.querySelectorAll('[name="add"]');

    addToCartButtons.forEach(button => {
      button.addEventListener('click', () => {
        showNotification('✓ Added to cart!', 'success');
      });
    });
  }

  function showNotification(message, type = 'success') {
    const notification = document.createElement('div');
    notification.className = `notification notification-${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'success' ? '#4caf50' : '#f44336'};
      color: white;
      padding: 15px 25px;
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.3);
      z-index: 10000;
      animation: slideInRight 0.3s ease-out;
    `;

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.animation = 'slideOutRight 0.3s ease-out';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  // ============================================
  // 11. TESTIMONIAL CAROUSEL AUTO-ROTATE
  // ============================================
  function initTestimonialCarousel() {
    const container = document.querySelector('.testimonials-container');
    if (!container || window.innerWidth > 768) return;

    const cards = container.querySelectorAll('.testimonial-card');
    let currentIndex = 0;

    setInterval(() => {
      cards[currentIndex].style.opacity = '0.5';
      cards[currentIndex].style.transform = 'scale(0.9)';

      currentIndex = (currentIndex + 1) % cards.length;

      cards[currentIndex].style.opacity = '1';
      cards[currentIndex].style.transform = 'scale(1)';
      cards[currentIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }, 5000);
  }

  // ============================================
  // 12. PRELOAD CRITICAL RESOURCES
  // ============================================
  function preloadCriticalResources() {
    // Preload hero video
    const heroVideo = document.querySelector('.hero-video video');
    if (heroVideo) {
      heroVideo.preload = 'metadata';
    }

    // Preload first 4 product images
    const productImages = document.querySelectorAll('.product-card-image');
    productImages.forEach((img, index) => {
      if (index < 4) {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = img.src;
        document.head.appendChild(link);
      }
    });
  }

  // ============================================
  // 13. PERFORMANCE MONITORING
  // ============================================
  function monitorPerformance() {
    if ('PerformanceObserver' in window) {
      // Monitor Largest Contentful Paint
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        console.log('LCP:', lastEntry.renderTime || lastEntry.loadTime);
      });

      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

      // Monitor First Input Delay
      const fidObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          console.log('FID:', entry.processingStart - entry.startTime);
        });
      });

      fidObserver.observe({ entryTypes: ['first-input'] });
    }
  }

  // ============================================
  // 14. INITIALIZE ALL FEATURES
  // ============================================
  function init() {
    // Wait for DOM to be fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', runInit);
    } else {
      runInit();
    }
  }

  function runInit() {
    try {
      initScrollProgress();
      initScrollAnimations();
      initCountdownTimer();
      initFAQAccordion();
      initBackToTop();
      initLazyLoading();
      initRippleEffect();
      initCardTilt();
      initSmoothAnchors();
      initAddToCartNotification();
      initTestimonialCarousel();
      preloadCriticalResources();

      // Performance monitoring only in development
      if (window.location.hostname === 'localhost' || window.Shopify?.designMode) {
        monitorPerformance();
      }

      console.log('✅ Mindshift Interactions initialized');
    } catch (error) {
      console.error('Error initializing interactions:', error);
    }
  }

  // Start initialization
  init();

})();

// ============================================
// 15. RIPPLE EFFECT CSS (Injected)
// ============================================
const rippleStyle = document.createElement('style');
rippleStyle.textContent = `
  .ripple-effect {
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.6);
    transform: scale(0);
    animation: ripple-animation 0.6s ease-out;
    pointer-events: none;
  }

  @keyframes ripple-animation {
    to {
      transform: scale(4);
      opacity: 0;
    }
  }

  @keyframes slideInRight {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes slideOutRight {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(100%);
      opacity: 0;
    }
  }
`;
document.head.appendChild(rippleStyle);
